import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import menuService from '../services/menuService'
import { Menu } from '../types/menu'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'

interface MenuListProps {
  restaurantId: string
  isOwner?: boolean
  onMenuGenerated?: () => void
  onMenuCreated?: () => void
}

const MenuList: React.FC<MenuListProps> = ({ restaurantId, isOwner = false, onMenuGenerated, onMenuCreated }) => {
  const { t } = useTranslation()
  const [menus, setMenus] = useState<Menu[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    fetchMenus()
  }, [restaurantId])

  useEffect(() => {
    const handleMenuCreated = () => {
      fetchMenus()
      onMenuCreated?.()
    }
    window.addEventListener('menu-created', handleMenuCreated)
    return () => window.removeEventListener('menu-created', handleMenuCreated)
  }, [restaurantId, onMenuCreated])

  const fetchMenus = async () => {
    try {
      setIsLoading(true)
      const data = await menuService.getMenus(restaurantId)
      setMenus(data)
    } catch (err: any) {
      console.error('Failed to fetch menus:', err)
      toast.error(t('toast.menusLoadFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleGenerateMenu = async () => {
    try {
      setIsGenerating(true)
      await menuService.generateMenu({ restaurantId })
      toast.success(t('toast.menuGenerated'))
      fetchMenus()
      if (onMenuGenerated) {
        onMenuGenerated()
      }
    } catch (err: any) {
      console.error('Failed to generate menu:', err)
      toast.error(err.response?.data?.message || t('toast.menuGenerateFailed'))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDeleteMenu = async (e: React.MouseEvent, menuId: string) => {
    e.preventDefault()
    e.stopPropagation()
    if (!window.confirm(t('menu.list.deleteConfirm'))) return
    try {
      await menuService.deleteMenu(menuId)
      toast.success(t('toast.menuDeleted'))
      fetchMenus()
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.menuDeleteFailed'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-white">{t('menu.list.title')}</h2>
        <button
          onClick={handleGenerateMenu}
          disabled={isGenerating}
          className="px-4 py-2 bg-black hover:bg-black/80 text-white rounded-full font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? t('menu.list.generating') : t('menu.list.generate')}
        </button>
      </div>

      {menus.length === 0 ? (
        <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4">🍽️</div>
          <h3 className="text-xl font-bold text-white mb-2">{t('menu.list.empty.title')}</h3>
          <p className="text-white/70 mb-6">{t('menu.list.empty.body')}</p>
          <button
            onClick={handleGenerateMenu}
            disabled={isGenerating}
            className="px-6 py-3 bg-black hover:bg-black/80 text-white rounded-full font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? t('menu.list.generating') : t('menu.list.generate')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {menus.map((menu) => (
            <Link
              key={menu._id || menu.id}
              to={`/menu/${menu._id || menu.id}`}
              className="bg-[#2a2a2a] border border-white/10 rounded-xl p-6 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:border-white/20 relative"
            >
              {isOwner && (
                <button
                  type="button"
                  onClick={(e) => handleDeleteMenu(e, menu._id || menu.id!)}
                  className="absolute top-3 right-3 text-red-400 text-xs font-semibold z-10"
                >
                  {t('menu.list.delete')}
                </button>
              )}
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-white truncate flex-1">
                  {menu.name}
                </h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  menu.status === 'active' 
                    ? 'bg-green-500 text-white' 
                    : menu.status === 'draft'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-500 text-white'
                }`}>
                  {menu.status || t('common.status.draft')}
                </span>
              </div>
              
              {menu.description && (
                <p className="text-white/70 text-sm mb-4 line-clamp-2">
                  {menu.description}
                </p>
              )}

              <div className="flex items-center justify-between text-sm text-white/60">
                <span>
                  {menu.type === 'custom'
                    ? t('menu.list.count.dishes', { count: String(menu.dishes?.length ?? 0) })
                    : t('menu.list.count.items', { count: String(menu.items?.length ?? 0) })}
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default MenuList
